import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import { AccountError, AccountSkeleton } from 'pages/booking/account/account-utils';
import { BOOKING_ACCOUNT_CREDIT_PURCHASE_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDateTime } from 'lang/date-time';
import { creditPurchaseStatusLabel } from 'pages/booking/account/credit-purchase-status';

/** Credit bundle purchase history for the signed-in user. */
export default function AccountCreditPurchases() {
  const { getAction } = useApi();
  const { _t, _tn, lang } = useLang();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const load = useCallback(
    async (cursor: string | null = null) => {
      setLoading(true);
      setError(false);
      try {
        const response: any = await getAction('GetReservationCreditPurchases')({
          query: { limit: 20, ...(cursor ? { cursor } : {}) },
        });
        setPurchases((current) => (cursor ? [...current, ...(response.purchases ?? [])] : response.purchases ?? []));
        setNextCursor(response.nextCursor ?? null);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [getAction]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <BookingAccountLayout>
      {loading && purchases.length === 0 && <AccountSkeleton />}
      {error && <AccountError retry={() => void load()} />}
      {!loading && !error && purchases.length === 0 && (
        <div className="account-state">
          <h2>{_t('No credit purchases yet')}</h2>
          <p>{_t('Your credit bundle purchases will appear here.')}</p>
        </div>
      )}
      <div className="account-card-list">
        {purchases.map((purchase) => (
          <article className="account-invoice-card" key={purchase.id}>
            <div>
              <p className="account-eyebrow">
                {_t('Purchase')} #{purchase.id} · {formatLocaleDateTime(purchase.createdAt, lang)}
              </p>
              <h2>{purchase.space.name}</h2>
              <p>{_tn('%d credit', '%d credits', Number(purchase.credits)).replace('%d', String(purchase.credits))}</p>
              <span className={`account-status account-status--${purchase.status}`}>
                {_t(creditPurchaseStatusLabel(purchase.status))}
              </span>
            </div>
            <div className="account-invoice-total">
              <strong>
                {Number(purchase.amountWithVat).toFixed(2)} {purchase.currencyCode}
              </strong>
              <button
                className="account-button account-button--secondary"
                onClick={() =>
                  navigate(BOOKING_ACCOUNT_CREDIT_PURCHASE_DETAIL_PATH.insert({ purchaseId: String(purchase.id) }), {
                    state: { purchase },
                  })
                }
              >
                {_t('View details')}
              </button>
            </div>
          </article>
        ))}
      </div>
      {nextCursor && (
        <button className="account-button account-load-more" disabled={loading} onClick={() => void load(nextCursor)}>
          {loading ? _t('Loading...') : _t('Load more')}
        </button>
      )}
    </BookingAccountLayout>
  );
}
